/**
 * GraphQL queries and hooks — serviceDependency pillar.
 * Split from graphql/queries.ts (issue #1409).
 */

import { gql } from '@apollo/client';
import { useQuery, type QueryResult } from '@apollo/client/react';
import { SERVICE_FIELDS } from './service';
import type { Service } from '../types/service';

/**
 * Fragment for ServiceDependency fields
 */
export const SERVICE_DEPENDENCY_FIELDS = gql`
  fragment ServiceDependencyFields on ServiceDependency {
    fromServiceId
    toServiceId
    metadata {
      dependencyType
      communicationPattern
      isCircuitBroken
      averageCallsPerMinute
      averageLatencyMs
      criticality
      notes
    }
    _createdAt
    _createdBy
    _updatedAt
    _updatedBy
  }
`;

/**
 * Query to get dependencies for a service (services this service depends on)
 */
export const GET_SERVICE_DEPENDENCIES = gql`
  ${SERVICE_DEPENDENCY_FIELDS}
  ${SERVICE_FIELDS}
  query GetServiceDependencies($serviceId: ID!) {
    serviceDependencies(serviceId: $serviceId) {
      ...ServiceDependencyFields
      toService {
        ...ServiceFields
      }
    }
  }
`;

/**
 * Query to get dependents for a service (services that depend on this service)
 */
export const GET_SERVICE_DEPENDENTS = gql`
  ${SERVICE_DEPENDENCY_FIELDS}
  ${SERVICE_FIELDS}
  query GetServiceDependents($serviceId: ID!) {
    serviceDependents(serviceId: $serviceId) {
      ...ServiceDependencyFields
      fromService {
        ...ServiceFields
      }
    }
  }
`;

/**
 * Hook to get service dependencies (services this service depends on)
 */
export function useServiceDependencies(serviceId: string): QueryResult<{
  serviceDependencies: Array<{
    fromServiceId: string;
    toServiceId: string;
    metadata: {
      dependencyType: string;
      communicationPattern: string;
      isCircuitBroken?: boolean | null;
      averageCallsPerMinute?: number | null;
      averageLatencyMs?: number | null;
      criticality?: string | null;
      notes?: string | null;
    };
    toService: Service;
    _createdAt: string;
    _createdBy: string;
    _updatedAt?: string | null;
    _updatedBy?: string | null;
  }>;
}> {
  return useQuery(GET_SERVICE_DEPENDENCIES, {
    variables: { serviceId },
    skip: !serviceId,
  });
}

/**
 * Hook to get service dependents (services that depend on this service)
 */
export function useServiceDependents(serviceId: string): QueryResult<{
  serviceDependents: Array<{
    fromServiceId: string;
    toServiceId: string;
    metadata: {
      dependencyType: string;
      communicationPattern: string;
      isCircuitBroken?: boolean | null;
      averageCallsPerMinute?: number | null;
      averageLatencyMs?: number | null;
      criticality?: string | null;
      notes?: string | null;
    };
    fromService: Service;
    _createdAt: string;
    _createdBy: string;
    _updatedAt?: string | null;
    _updatedBy?: string | null;
  }>;
}> {
  return useQuery(GET_SERVICE_DEPENDENTS, {
    variables: { serviceId },
    skip: !serviceId,
  });
}

/**
 * Query for the Dependency Health dashboard widget (issue #1651).
 */
export const GET_DEPENDENCY_HEALTH = gql`
  query GetDependencyHealth {
    dependencyHealth {
      summary {
        totalDependencies
        healthy
        warnings
        critical
      }
      issues {
        type
        severity
        service
        dependsOn
        message
      }
    }
  }
`;

export interface DependencyHealthIssue {
  type: string;
  severity: string;
  service: string;
  dependsOn: string;
  message: string;
}

export interface DependencyHealthData {
  dependencyHealth: {
    summary: {
      totalDependencies: number;
      healthy: number;
      warnings: number;
      critical: number;
    };
    issues: DependencyHealthIssue[];
  };
}

/**
 * Hook for the Dependency Health widget's team-scoped summary + issue list.
 */
export function useDependencyHealth(): QueryResult<DependencyHealthData> {
  return useQuery(GET_DEPENDENCY_HEALTH, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}
